import {graphql, useFragment} from 'react-relay'
import {CreateIssueDialogEntry, type CreateIssueDialogEntryProps} from '@github-ui/issue-create/CreateIssueDialogEntry'
import {readProjectFromFragment} from '@github-ui/item-picker/ProjectPicker'
import {readMilestoneFromFragment} from '@github-ui/item-picker/MilestonePicker'
import type {SubIssuesCreateDialog$key} from './__generated__/SubIssuesCreateDialog.graphql'
import {noop} from '@github-ui/noop'
import {useAlert} from '../utils/use-alert'
import {useCallback, useMemo} from 'react'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import type {AppPayload} from '../types/app-payload'
import {RelationshipsAlertDialog} from '@github-ui/issue-viewer/RelationshipsAlertDialog'

export function SubIssuesCreateDialog({
  issue,
  open,
  setOpen,
  onCreateSuccess,
}: {
  issue: SubIssuesCreateDialog$key
  open: boolean
  setOpen: (open: boolean) => void
  onCreateSuccess: CreateIssueDialogEntryProps['onCreateSuccess']
}) {
  const {alert, resetAlert, showServerAlert} = useAlert()
  const appPayload = useAppPayload<AppPayload>()
  const data = useFragment(
    // !! NOTE: This fragment is preloaded on the server via the primary IssueViewer query, so the data
    // here is loaded whether the flag is enabled or not. Please be careful and don't include fields which
    // are not already loaded by other fragments.
    graphql`
      fragment SubIssuesCreateDialog on Issue {
        id
        repository {
          id
          name
          owner {
            login
          }
        }
        projectItemsNext(first: 10) @connection(key: "ProjectSection_projectItemsNext") {
          edges {
            node {
              project {
                ...ProjectPickerProject
                closed
              }
            }
          }
        }
        milestone {
          ...MilestonePickerMilestone
        }
      }
    `,
    issue,
  )

  const onCreateError = useCallback((error: Error) => showServerAlert(error), [showServerAlert])

  // Extract projects from the parent issue to inherit them in the sub-issue
  const preselectedProjects = useMemo(() => {
    const projectItems = data.projectItemsNext?.edges ?? []
    return projectItems
      .flatMap(edge => (edge?.node ? [edge.node] : []))
      .filter(projectItem => projectItem.project.closed !== true)
      .map(projectItem => readProjectFromFragment(projectItem.project))
  }, [data.projectItemsNext?.edges])

  // Extract milestone from the parent issue to inherit it in the sub-issue
  // Only inherit if the milestone exists (milestones are repository-scoped, so this is always valid for sub-issues)
  const preselectedMilestone = useMemo(() => {
    return data.milestone ? readMilestoneFromFragment(data.milestone) : undefined
  }, [data.milestone])

  const issueCreateProps: CreateIssueDialogEntryProps = {
    navigate: noop,
    onCreateSuccess,
    onCreateError,
    isCreateDialogOpen: open,
    setIsCreateDialogOpen: setOpen,
    optionConfig: {
      useMonospaceFont: appPayload?.current_user_settings?.use_monospace_font ?? false,
      scopedOrganization: data.repository.owner.login,
      issueCreateArguments: {
        repository: {
          owner: data.repository.owner.login,
          name: data.repository.name,
        },
        parentIssue: {
          id: data.id,
        },
        initialValues: {
          projects: preselectedProjects,
          milestone: preselectedMilestone,
        },
      },
    },
  }

  return (
    <>
      {alert && (
        <RelationshipsAlertDialog title={alert.title} onClose={resetAlert}>
          {alert.body}
        </RelationshipsAlertDialog>
      )}
      <CreateIssueDialogEntry {...issueCreateProps} />
    </>
  )
}

try{ SubIssuesCreateDialog.displayName ||= 'SubIssuesCreateDialog' } catch {}